/** Staff list + role filter (ST-01). Owner/manager only (route-guarded).
 *  Rows are clickable → EmployeeDetailDrawer (detail + payroll). Adds a
 *  Плата (бруто) column showing the gross salary in minor units via <Money>. */
import type { Role } from '@gym/shared';
import { UserPlus, Users } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  EmptyState,
  Money,
  Select,
  StatusPill,
  Table,
  TableSkeleton,
  TableWrap,
  TD,
  TH,
  THead,
  Toolbar,
  TR,
  useFormatters,
} from '../../components/ui.js';

import { EmployeeDetailDrawer } from './EmployeeDetailDrawer.js';
import { type StaffRow, useStaffList } from './hooks/useStaff.js';
import { StaffForm } from './StaffForm.js';

export function StaffList(): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [roleFilter, setRoleFilter] = useState<Role | ''>('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<StaffRow | null>(null);
  const list = useStaffList(roleFilter ? { appRole: roleFilter } : {});
  const rows = list.data ?? [];

  return (
    <div className="grid gap-4">
      <Toolbar className="rounded-xl border border-border bg-surface p-3">
        <Select
          aria-label={t('staff.form.role')}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as Role | '')}
          className="w-auto"
        >
          <option value="">{t('common.all')}</option>
          {(['owner', 'manager', 'front_desk', 'trainer'] as const).map((r) => (
            <option key={r} value={r}>
              {t('staff.roles.' + r)}
            </option>
          ))}
        </Select>
        <div className="flex-1" />
        <Button
          variant="brand"
          icon={<UserPlus className="h-4 w-4" />}
          onClick={() => setCreating(true)}
        >
          {t('staff.form.create_title')}
        </Button>
      </Toolbar>

      <div className="overflow-hidden rounded-xl border border-border bg-surface">
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>{t('staff.form.first_name')}</TH>
                <TH>{t('staff.form.role')}</TH>
                <TH>{t('auth.email')}</TH>
                <TH>{t('staff.form.hired_at')}</TH>
                <TH numeric>{t('staff.form.gross_salary')}</TH>
                <TH numeric>{t('common.actions')}</TH>
              </tr>
            </THead>
            {list.isLoading ? (
              <TableSkeleton rows={5} cols={6} />
            ) : (
              <tbody>
                {rows.map((s: StaffRow) => (
                  <TR key={s.id} clickable onClick={() => setSelected(s)}>
                    <TD className="font-medium">
                      {s.first_name} {s.last_name}
                    </TD>
                    <TD className="text-muted">{t('staff.roles.' + s.app_role)}</TD>
                    <TD className="text-muted">{s.email ?? '—'}</TD>
                    <TD className="text-muted">{s.hired_at ? fmt.date(s.hired_at) : '—'}</TD>
                    <TD numeric>
                      {s.gross_salary != null ? <Money minor={s.gross_salary} /> : '—'}
                    </TD>
                    <TD numeric>
                      <div className="flex items-center justify-end gap-2">
                        <StatusPill
                          status={s.is_active ? 'active' : 'inactive'}
                          label={s.is_active ? t('common.active') : t('common.inactive')}
                        />
                      </div>
                    </TD>
                  </TR>
                ))}
              </tbody>
            )}
          </Table>
        </TableWrap>
        {!list.isLoading && rows.length === 0 ? (
          <div className="p-6">
            <EmptyState icon={Users} title={t('staff.list.empty')} />
          </div>
        ) : null}
      </div>

      <EmployeeDetailDrawer
        open={selected != null}
        onOpenChange={(o) => {
          if (!o) setSelected(null);
        }}
        staff={selected}
      />

      {/* Add employee — same popup pattern as members/products */}
      <StaffForm open={creating} onOpenChange={setCreating} />
    </div>
  );
}
